import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Command } from 'app/models/command';
import { SiteReviewService } from 'app/services/site-review.service';
import { PhaseWorkspace } from './core/phase-types';

export type PhaseConfigStatus = 'Draft' | 'Published' | 'Archived';

/** Version metadata (no configuration body). */
export interface PhaseConfigVersionInfo {
  id: number;
  number: number;
  name: string;
  notes: string;
  status: PhaseConfigStatus;
  seedVersion: number | null;
  publishedAt: string;
  createdDate: string;
  updatedDate: string;
  createdByName: string;
  updatedByName: string;
  publishedByName: string;
}

/** Phase or subphase name of the configuration behind the Active generation. */
export interface PhaseNameRow {
  kind: 'phase' | 'sub';
  phaseCode: string;
  subCode: string | null;
  name: string;
  color: string | null;
  versionId: number;
  /** District the phase belongs to (1 = NorCal, 2 = SoCal) */
  district: number;
}

/** Stored rules-based phase of one job. */
export interface JobPhaseRow {
  jobId: number;
  phaseCode: string | null;
  subCode: string | null;
  status: string;
  /** true when the stored result is older than the job data and could not be refreshed */
  stale: boolean;
  versionId: number;
}

export interface PhaseConfigVersionWithConfig extends PhaseConfigVersionInfo {
  config: PhaseWorkspace;
}

/**
 * Calls the dbo.cmdPhaseConfig* stored procedures through /api/cmd.
 *
 * Parameters are built by hand instead of Command.addParameter: the dispatcher only strips the
 * outer apostrophes of a value, so inner apostrophes must stay as they are (addParameter would
 * double the first one and corrupt the JSON).
 */
@Injectable({
  providedIn: 'root'
})
export class PhaseConfigApi {
  private readonly api = inject(SiteReviewService);

  async list(): Promise<PhaseConfigVersionInfo[]> {
    const rows = await this.run('cmdPhaseConfigList', []);
    return rows.map(r => this.toInfo(r));
  }

  /** One version with its configuration; no id means the Published version (null when none). */
  async get(id?: number | null): Promise<PhaseConfigVersionWithConfig | null> {
    const rows = await this.run('cmdPhaseConfigGet', [this.int('PhaseConfigVersionID', id ?? null)]);
    const row = rows[0];
    if (!row) {
      return null;
    }
    const text = row.configJson ?? row.ConfigJson;
    return { ...this.toInfo(row), config: JSON.parse(String(text)) as PhaseWorkspace };
  }

  /** Creates a Draft (no id) or updates the given Draft. */
  async save(input: {
    id: number | null;
    name: string;
    notes: string;
    config: PhaseWorkspace;
    seedVersion: number | null;
  }): Promise<PhaseConfigVersionInfo> {
    const rows = await this.run('cmdPhaseConfigSave', [
      this.int('PhaseConfigVersionID', input.id),
      this.text('Name', input.name),
      this.text('Notes', input.notes),
      this.text('ConfigJson', JSON.stringify(input.config)),
      this.int('SeedVersion', input.seedVersion)
    ]);
    return this.toInfo(rows[0]);
  }

  async duplicate(id: number, name?: string): Promise<PhaseConfigVersionInfo> {
    const rows = await this.run('cmdPhaseConfigDuplicate', [
      this.int('PhaseConfigVersionID', id),
      this.text('Name', name || null)
    ]);
    return this.toInfo(rows[0]);
  }

  async publish(id: number): Promise<PhaseConfigVersionInfo> {
    const rows = await this.run('cmdPhaseConfigPublish', [this.int('PhaseConfigVersionID', id)]);
    return this.toInfo(rows[0]);
  }

  /** Opens a Building generation for the version (admin). The generation is not visible to readers until it is activated. */
  async generationStart(versionId: number): Promise<{ generationId: number; versionId: number }> {
    const rows = await this.run('cmdPhaseConfigGenerationStart', [this.int('PhaseConfigVersionID', versionId)]);
    const r = rows[0];
    if (!r) {
      throw new Error('The server did not return the new generation.');
    }
    return { generationId: Number(r.generationID), versionId: Number(r.phaseConfigVersionID) };
  }

  /** Evaluates the next jobs of a Building generation. Call until remaining is 0. */
  async generationBuild(generationId: number, max: number, batchSize: number): Promise<{ evaluated: number; seconds: number; remaining: number }> {
    const rows = await this.run('cmdPhaseConfigGenerationBuild', [
      this.int('GenerationID', generationId),
      this.int('Max', max),
      this.int('BatchSize', batchSize)
    ]);
    const r = rows[0];
    if (!r) {
      throw new Error('The server did not return the build progress.');
    }
    return { evaluated: Number(r.evaluated ?? 0), seconds: Number(r.seconds ?? 0), remaining: Number(r.remaining ?? 0) };
  }

  /** Makes a complete generation the Active one in a single transaction. */
  async generationActivate(generationId: number): Promise<{ jobs: number; transitions: number }> {
    const rows = await this.run('cmdPhaseConfigGenerationActivate', [this.int('GenerationID', generationId)]);
    const r = rows[0];
    return { jobs: Number(r?.jobs ?? 0), transitions: Number(r?.transitions ?? 0) };
  }

  /** Phase and subphase names of the Active generation's version. Open to any signed-in user. */
  async getNames(): Promise<PhaseNameRow[]> {
    const rows = await this.run('cmdPhaseConfigGetNames', []);
    return rows.map(r => ({
      kind: String(r.kind) === 'sub' ? 'sub' : 'phase',
      phaseCode: String(r.phaseCode ?? ''),
      subCode: r.subCode == null ? null : String(r.subCode),
      name: String(r.name ?? ''),
      color: r.color == null ? null : String(r.color),
      versionId: Number(r.phaseConfigVersionID),
      district: r.district == null ? 1 : Number(r.district)
    }));
  }

  /**
   * Stored phase of the given jobs (null = every stored job, never recalculated). With refresh, the server first
   * recalculates the jobs whose stored result is out of date. Open to any signed-in user.
   */
  async getJobPhases(jobIds: number[] | null, refresh: boolean): Promise<JobPhaseRow[]> {
    const rows = await this.run('cmdPhaseConfigGetJobPhases', [
      this.text('JobIDs', jobIds && jobIds.length ? JSON.stringify(jobIds) : null),
      this.int('Refresh', refresh ? 1 : 0)
    ]);
    return rows.map(r => ({
      jobId: Number(r.jobID),
      phaseCode: r.phaseCode == null ? null : String(r.phaseCode),
      subCode: r.subCode == null ? null : String(r.subCode),
      status: String(r.status ?? ''),
      stale: r.stale === true || r.stale === 1 || r.stale === '1' || r.stale === 'true',
      versionId: Number(r.phaseConfigVersionID)
    }));
  }

  async remove(id: number): Promise<void> {
    await this.run('cmdPhaseConfigDelete', [this.int('PhaseConfigVersionID', id)]);
  }

  /** Readable message from a failed call. */
  static messageOf(e: any): string {
    const candidates = [e?.error?.message, e?.error?.Message, typeof e?.error === 'string' ? e.error : '', e?.message];
    const found = candidates.find(c => typeof c === 'string' && c.trim());
    if (found) {
      return String(found).slice(0, 300);
    }
    return 'The server could not complete the request.';
  }

  // ── Internals ───────────────────────────────────────────────────────────────────────

  private async run(procedure: string, parameters: string[]): Promise<any[]> {
    const cmd = new Command();
    cmd.procedure = procedure;
    cmd.parameters = parameters;
    const result = await firstValueFrom(this.api.command(cmd));
    return Array.isArray(result) ? result : [];
  }

  private text(key: string, value: string | null | undefined): string {
    return '@' + key + '=' + (value == null ? 'null' : "'" + value + "'");
  }

  private int(key: string, value: number | null | undefined): string {
    return '@' + key + '=' + (value == null ? 'null' : String(Math.trunc(value)));
  }

  private toInfo(r: any): PhaseConfigVersionInfo {
    const str = (v: any) => (v == null ? '' : String(v));
    const num = (v: any) => (v == null || v === '' ? null : Number(v));
    return {
      id: Number(r.phaseConfigVersionID),
      number: Number(r.versionNumber),
      name: str(r.name),
      notes: str(r.notes),
      status: str(r.status) as PhaseConfigStatus,
      seedVersion: num(r.seedVersion),
      publishedAt: str(r.publishedAt),
      createdDate: str(r.createdDate),
      updatedDate: str(r.updatedDate),
      createdByName: str(r.createdByName),
      updatedByName: str(r.updatedByName),
      publishedByName: str(r.publishedByName)
    };
  }
}
